<template>
  <div
    class="snooze-card"
    v-loading="loadingStatus"
    element-loading-background="rgba(15, 23, 42, 0.72)"
  >
    <div class="snooze-card-title">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <span>{{ isActive ? "警報已暫停" : "暫停警報至" }}</span>
    </div>

    <div
      v-if="unsupportedMessage"
      class="snooze-card-message snooze-card-message--warning"
    >
      {{ unsupportedMessage }}
    </div>

    <div
      v-else-if="statusError"
      class="snooze-card-message snooze-card-message--error"
    >
      {{ statusError }}
    </div>

    <div v-else-if="isActive">
      <div class="snooze-card-message snooze-card-message--success">
        <div>{{ snoozeMessage }}</div>
        <div v-if="formattedUntil" class="snooze-card-meta">
          將於 {{ formattedUntil }} 恢復
        </div>
      </div>

      <el-button
        class="snooze-card-primary"
        :loading="clearing"
        :disabled="busy"
        @click="handleClear"
      >
        立即恢復警報
      </el-button>
    </div>

    <div v-else>
      <el-form
        ref="snoozeForm"
        :model="ruleForm"
        :rules="rules"
        label-position="top"
        class="snooze-card-form"
      >
        <div class="snooze-card-grid">
          <el-form-item prop="untilDate" class="snooze-card-field">
            <label class="snooze-card-label">日期</label>
            <el-date-picker
              v-model="ruleForm.untilDate"
              type="date"
              placeholder="選擇日期"
              value-format="yyyy-MM-dd"
              format="yyyy/MM/dd"
              :picker-options="datePickerOptions"
              style="width: 100%"
              @change="handleDateChange"
            />
          </el-form-item>
          <el-form-item prop="untilTime" class="snooze-card-field">
            <label class="snooze-card-label">時間</label>
            <el-time-picker
              v-model="ruleForm.untilTime"
              placeholder="選擇時間"
              value-format="HH:mm:ss"
              format="HH:mm"
              :picker-options="timePickerOptions"
              style="width: 100%"
              @change="handleTimeChange"
            />
          </el-form-item>
        </div>
      </el-form>

      <p class="snooze-card-hint">
        警報將會暫停，並於所選時間自動恢復。
      </p>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import {
  clearPublicNotificationSnooze,
  getPublicNotificationSnooze,
  setPublicNotificationSnooze,
} from "@/api";

export default {
  name: "NotificationSnoozeCard",
  props: {
    token: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loadingStatus: false,
      submittingUntil: false,
      clearing: false,
      status: null,
      statusError: "",
      unsupportedMessage: "",
      ruleForm: {
        untilDate: "",
        untilTime: "",
      },
      rules: {
        untilDate: [{ validator: this.validateUntilDate, trigger: "change" }],
        untilTime: [{ validator: this.validateUntilTime, trigger: "change" }],
      },
    };
  },
  computed: {
    isActive() {
      return !!(this.status && this.status.active);
    },
    busy() {
      return this.loadingStatus || this.submittingUntil || this.clearing;
    },
    canSubmitUntil() {
      return !!this.ruleForm.untilDate && !!this.ruleForm.untilTime;
    },
    snoozeMessage() {
      if (this.status && this.status.message) {
        return this.status.message;
      }
      return "此警報目前已暫停。";
    },
    formattedUntil() {
      if (!this.status || !this.status.until) {
        return "";
      }
      const date = dayjs(this.status.until);
      if (!date.isValid()) {
        return this.status.until;
      }
      return date.format("YYYY/MM/DD HH:mm:ss");
    },
    datePickerOptions() {
      return {
        disabledDate: (time) => {
          const todayStart = dayjs().startOf("day");
          return dayjs(time).isBefore(todayStart, "day");
        },
      };
    },
    timePickerOptions() {
      return {
        selectableRange: this.getSelectableRange(),
      };
    },
  },
  watch: {
    token: {
      immediate: true,
      handler() {
        this.fetchStatus();
      },
    },
  },
  methods: {
    getNextWholeHour() {
      return dayjs().add(1, "hour").startOf("hour");
    },
    getDefaultTimeForDate(dateValue) {
      if (!dateValue) {
        return "";
      }
      const nextWholeHour = this.getNextWholeHour();
      if (dayjs(dateValue).isSame(dayjs(), "day")) {
        if (!nextWholeHour.isSame(dayjs(), "day")) {
          return "";
        }
        return nextWholeHour.format("HH:mm:ss");
      }
      return nextWholeHour.format("HH:mm:ss");
    },
    getSelectableRange() {
      const selectedDate = this.ruleForm.untilDate;
      const now = dayjs();
      if (selectedDate && dayjs(selectedDate).isSame(now, "day")) {
        const minTime = now.add(1, "minute").format("HH:mm:ss");
        return `${minTime} - 23:59:59`;
      }
      return "00:00:00 - 23:59:59";
    },
    validateUntilDate(rule, value, callback) {
      const hasTime = !!this.ruleForm.untilTime;
      if (!value && !hasTime) {
        callback();
        return;
      }
      if (!value) {
        callback(new Error("請選擇日期。"));
        return;
      }
      if (dayjs(value).isBefore(dayjs().startOf("day"), "day")) {
        callback(new Error("日期必須為今天或之後。"));
        return;
      }
      callback();
    },
    validateUntilTime(rule, value, callback) {
      const hasDate = !!this.ruleForm.untilDate;
      if (!value && !hasDate) {
        callback();
        return;
      }
      if (!value) {
        callback(new Error("請選擇時間。"));
        return;
      }
      if (!hasDate) {
        callback(new Error("請先選擇日期。"));
        return;
      }
      const timestamp = dayjs(`${this.ruleForm.untilDate}T${value}`);
      if (!timestamp.isValid()) {
        callback(new Error("請選擇有效時間。"));
        return;
      }
      if (!timestamp.isAfter(dayjs())) {
        callback(new Error("時間必須晚於現在。"));
        return;
      }
      callback();
    },
    emitStatus(extra = {}) {
      this.$emit("status-change", {
        active: this.isActive,
        until: this.status && this.status.until ? this.status.until : "",
        message: this.status && this.status.message ? this.status.message : "",
        supported: !this.unsupportedMessage,
        error: this.statusError,
        loaded: !this.loadingStatus,
        ...extra,
      });
    },
    normalizeErrorMessage(error, fallback) {
      if (!error) {
        return fallback;
      }
      const status = error.response && error.response.status;
      if (status === 400) {
        return "請求無效，請檢查連結或提交內容。";
      }
      if (status === 404) {
        return "找不到相關警報資訊，請確認連結是否正確或已過期。";
      }
      if (status) {
        return `請求失敗，狀態碼：${status}`;
      }
      return (
        error.message ||
        error.msg ||
        (error.data && error.data.message) ||
        fallback
      );
    },
    buildUntilTimestamp() {
      if (!this.canSubmitUntil) {
        return "";
      }
      const timestamp = dayjs(
        `${this.ruleForm.untilDate}T${this.ruleForm.untilTime}`
      );
      if (!timestamp.isValid()) {
        return "";
      }
      if (!timestamp.isAfter(dayjs())) {
        return "";
      }
      return timestamp.format("YYYY-MM-DDTHH:mm:ssZ");
    },
    hasPendingSnoozeInput() {
      return !!this.ruleForm.untilDate || !!this.ruleForm.untilTime;
    },
    handleDateChange(value) {
      if (!value) {
        this.ruleForm.untilTime = "";
      } else if (!this.ruleForm.untilTime) {
        this.ruleForm.untilTime = this.getDefaultTimeForDate(value);
      }
      this.$nextTick(() => {
        if (this.$refs.snoozeForm) {
          this.$refs.snoozeForm.validateField("untilDate");
          this.$refs.snoozeForm.validateField("untilTime");
        }
      });
    },
    handleTimeChange() {
      this.$nextTick(() => {
        if (this.$refs.snoozeForm) {
          this.$refs.snoozeForm.validateField("untilTime");
        }
      });
    },
    validatePendingSnoozeInput() {
      if (!this.hasPendingSnoozeInput()) {
        return Promise.resolve(true);
      }
      return new Promise((resolve) => {
        this.$refs.snoozeForm.validate((valid) => {
          if (!valid) {
            resolve(false);
            return;
          }
          resolve(!!this.buildUntilTimestamp());
        });
      });
    },
    async submitIfNeeded() {
      if (this.isActive || !this.hasPendingSnoozeInput()) {
        return { skipped: true };
      }
      const isValid = await this.validatePendingSnoozeInput();
      if (!isValid) {
        return { skipped: false, valid: false };
      }
      const success = await this.handleSnoozeUntil();
      return { skipped: false, valid: success, submitted: success };
    },
    async fetchStatus() {
      this.status = null;
      this.statusError = "";
      this.unsupportedMessage = "";

      if (!this.token) {
        this.emitStatus({ loaded: true, supported: false });
        return;
      }

      this.loadingStatus = true;
      try {
        const data = await getPublicNotificationSnooze(this.token);
        this.status = data || null;
        this.emitStatus();
      } catch (error) {
        const message = this.normalizeErrorMessage(
          error,
          "無法載入暫停狀態。"
        );
        if (
          typeof message === "string" &&
          message.includes("legacy notification token does not support snooze")
        ) {
          this.unsupportedMessage = message;
          this.emitStatus({ supported: false, error: "" });
          return;
        }
        this.statusError = message;
        this.emitStatus();
      } finally {
        this.loadingStatus = false;
        this.emitStatus();
      }
    },
    async handleSnoozeUntil() {
      const until = this.buildUntilTimestamp();
      if (!until) {
        this.notify("warning", "請先選擇有效的日期和時間。");
        return false;
      }

      this.submittingUntil = true;
      try {
        const data = await setPublicNotificationSnooze(this.token, {
          mode: "until",
          until,
        });
        this.status = data || null;
        this.ruleForm.untilDate = "";
        this.ruleForm.untilTime = "";
        if (this.$refs.snoozeForm) {
          this.$refs.snoozeForm.resetFields();
        }
        this.notify("success", this.snoozeMessage);
        this.emitStatus();
        return true;
      } catch (error) {
        this.notify(
          "error",
          this.normalizeErrorMessage(error, "暫停警報失敗。")
        );
        return false;
      } finally {
        this.submittingUntil = false;
      }
    },
    async handleClear() {
      this.clearing = true;
      try {
        const data = await clearPublicNotificationSnooze(this.token);
        this.status = { active: false, message: data && data.message };
        this.notify(
          "success",
          (data && data.message) || "警報暫停已成功解除。"
        );
        this.emitStatus();
      } catch (error) {
        this.notify(
          "error",
          this.normalizeErrorMessage(error, "恢復警報失敗。")
        );
      } finally {
        this.clearing = false;
      }
    },
    notify(type, message) {
      if (this.$message && typeof this.$message[type] === "function") {
        this.$message[type](message);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.snooze-card {
  margin-top: 1rem;
}

.snooze-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  color: #cbd5e1;
  font-size: 1rem;
  font-weight: 500;
}

.snooze-card-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.snooze-card-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.snooze-card-label {
  color: #94a3b8;
  font-size: 0.95rem;
}

.snooze-card-hint {
  margin-top: 0.75rem;
  color: #94a3b8;
  font-size: 0.875rem;
  line-height: 1.4;
}

.snooze-card-message {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.875rem;
  border: 1px solid transparent;
  line-height: 1.5;
}

.snooze-card-message--success {
  border-color: rgba(52, 211, 153, 0.2);
  background: rgba(6, 78, 59, 0.35);
  color: #d1fae5;
}

.snooze-card-message--warning {
  border-color: rgba(251, 191, 36, 0.25);
  background: rgba(120, 53, 15, 0.3);
  color: #fde68a;
}

.snooze-card-message--error {
  border-color: rgba(248, 113, 113, 0.25);
  background: rgba(127, 29, 29, 0.3);
  color: #fecaca;
}

.snooze-card-meta {
  margin-top: 0.35rem;
  color: #a7f3d0;
  font-size: 0.9rem;
}

.snooze-card-primary {
  width: 100%;
  margin: 0;
}

@media (max-width: 640px) {
  .snooze-card-grid {
    grid-template-columns: 1fr;
  }

  .snooze-card-primary {
    min-width: 0;
  }
}
</style>
